/**
 * Lyric Boost Design System - Color Tokens
 * 
 * Centralized color definitions used throughout the application.
 * These tokens provide semantic naming and ensure consistency.
 */

export const colors = {
  // === Primary Colors ===
  primary: {
    main: '#6366f1',      // Bright indigo - primary interactive elements (buttons, links)
    dark: '#4f46e5',      // Darker indigo - hover states
    light: '#818cf8',     // Lighter indigo - secondary states
    contrastText: '#ffffff',
  },

  // === Secondary Colors ===
  secondary: {
    main: '#a855f7',      // Vibrant purple - accent elements
    dark: '#9333ea',      // Darker purple - hover states
    light: '#c084fc',     // Lighter purple - subtle accents
  },

  // === Background Colors ===
  background: {
    primary: '#0a0a0a',    // Darkest background - main app background
    secondary: '#111827',   // Dark background - cards, paper elements
    tertiary: '#1c2230',    // Medium dark - elevated surfaces
    elevated: '#202538',    // Slightly elevated - inputs, toolbars
    paper: 'rgba(17, 24, 39, 1)', // Semi-transparent dark for overlays
  },

  // === Text Colors ===
  text: {
    primary: '#ffffff',     // White - headings, important text
    secondary: '#d1d5db',   // Light gray - body text
    tertiary: '#9ca3af',    // Medium gray - helper text, captions
    muted: '#a0a0a0',      // Muted gray - timestamps, metadata
    disabled: 'rgba(255, 255, 255, 0.5)', // Disabled state text
  },

  // === Border & Divider Colors ===
  border: {
    subtle: 'rgba(255, 255, 255, 0.05)',   // Very subtle borders
    default: 'rgba(255, 255, 255, 0.1)',    // Default borders
    medium: 'rgba(255, 255, 255, 0.2)',     // Medium emphasis borders
    strong: 'rgba(255, 255, 255, 0.3)',     // Strong borders
  },

  // === Surface Colors (for cards, containers with backgrounds) ===
  surface: {
    transparent: 'rgba(255, 255, 255, 0.02)',  // Subtle surface with transparency
    subtle: 'rgba(255, 255, 255, 0.05)',       // Subtle surface
    default: 'rgba(255, 255, 255, 0.08)',      // Default surface
    hover: 'rgba(255, 255, 255, 0.1)',         // Hover state surface
  },

  // === Semantic Colors ===
  semantic: {
    success: '#059669',     // Green - success states
    successDark: '#047857', // Darker green - success hover
    error: '#d32f2f',       // Red - error states
    errorDark: '#b71c1c',   // Darker red - error hover
    warning: '#f59e0b',     // Orange - warning states
    info: '#3b82f6',        // Blue - info states
  },

  // === Tag Colors ===
  tags: {
    default: {
      background: 'rgba(255, 255, 255, 0.08)',
      color: 'rgba(255, 255, 255, 0.9)',
      border: 'rgba(255, 255, 255, 0.2)',
    },
    purple: {
      background: 'rgba(124, 58, 237, 0.2)',
      color: '#a78bfa',
      border: 'rgba(124, 58, 237, 0.3)',
    },
    indigo: {
      background: 'rgba(99, 102, 241, 0.2)',
      color: '#818cf8',
      border: 'rgba(99, 102, 241, 0.3)',
    },
  },

  // === Gradient Definitions ===
  gradients: {
    primary: 'linear-gradient(to bottom, #0a0a0a, #111827)',
    purpleBlue: 'linear-gradient(to right, #a78bfa, #3b82f6)',
    text: 'linear-gradient(to right, #a78bfa, #3b82f6)', // For gradient text effects
  },

  // === Focus & Active States ===
  states: {
    focus: 'rgba(99, 102, 241, 0.2)',  // Focus ring color
    active: 'rgba(99, 102, 241, 0.1)',  // Active state background
    selected: 'rgba(99, 102, 241, 0.15)', // Selected state
  },
} as const;

// Type export for TypeScript autocomplete
export type ColorTokens = typeof colors;
